import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from '../../icons/arrow-right.svg';
import splash1 from '../../images/organisation/team-tour-desktop.jpg';
import splash2 from '../../images/organisation/splash-2.jpg';
import splash3 from '../../images/organisation/splash-3.jpg';
import './organisation-splash.scss';

interface Slide {
  photo: string;
  title: string;
  text: string;
}

export const ORGANISATION_SPLASH_SLIDES: Slide[] = [
  {
    photo: splash1,
    title: 'Bring Dream to your company',
    text: 'Set up Dream for your whole team. Add secretaries for your key staff, all billed through one card.',
  },
  {
    photo: splash2,
    title: 'Their secretaries handle the hard stuff',
    text: 'You setup central tools for expenses, travel, and more. Your team can ask their secretary to handle the job, no new apps to learn.',
  },
  {
    photo: splash3,
    title: 'All the tools for you, and your secretaries',
    text: 'Empower your team’s secretaries to handle planning & purchases. Add Team Cards. Your secretaries can use them when they need to book or buy anything.',
  },
];

export const SPLASH_AUTOPLAY_MS = 6000;
const SWIPE_THRESHOLD_PX = 40;

interface OrganisationSplashProps {
  onCreate: () => void;
  /** Stops autoplay, e.g. while the create dialog is open */
  paused?: boolean;
}

/** Figma "Settings - Org / Splash 1–3": photo carousel above the create CTA. */
const OrganisationSplash: FC<OrganisationSplashProps> = ({
  onCreate,
  paused = false,
}) => {
  const [index, setIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const count = ORGANISATION_SPLASH_SLIDES.length;

  const go = useCallback(
    (delta: number) => setIndex((i) => (i + delta + count) % count),
    [count],
  );

  const reducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoplay = !paused && !hovering && !focused && !reducedMotion;

  useEffect(() => {
    if (!autoplay) return;
    const timer = window.setTimeout(() => go(1), SPLASH_AUTOPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [autoplay, index, go]);

  const slide = ORGANISATION_SPLASH_SLIDES[index];

  return (
    <div className='organisation-splash' data-testid='organisation-splash'>
      <h2 className='section-title'>Set up Dream for your organisation</h2>
      <section
        className={`organisation-splash-card slide-${index + 1}`}
        aria-roledescription='carousel'
        aria-label='About organisations'
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setFocused(false);
          }
        }}
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const start = touchStartX.current;
          touchStartX.current = null;
          const end = e.changedTouches[0]?.clientX;
          if (start === null || end === undefined) return;
          const dx = end - start;
          if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) go(dx < 0 ? 1 : -1);
        }}
      >
        <div className='organisation-splash-photo'>
          {ORGANISATION_SPLASH_SLIDES.map((s, i) => (
            <img
              key={s.photo}
              src={s.photo}
              alt=''
              className={i === index ? 'active' : undefined}
            />
          ))}
          <div className='organisation-splash-pagination'>
            <button
              type='button'
              className='organisation-splash-arrow'
              aria-label='Previous'
              onClick={() => go(-1)}
            >
              <ArrowLeftIcon aria-hidden='true' />
            </button>
            <div className='organisation-splash-dots'>
              {ORGANISATION_SPLASH_SLIDES.map((s, i) => (
                <button
                  key={s.title}
                  type='button'
                  className={`organisation-splash-dot${i === index ? ' active' : ''}`}
                  aria-label={`Show slide ${i + 1} of ${count}`}
                  aria-current={i === index ? 'true' : undefined}
                  onClick={() => setIndex(i)}
                />
              ))}
            </div>
            <button
              type='button'
              className='organisation-splash-arrow'
              aria-label='Next'
              onClick={() => go(1)}
            >
              <ArrowRightIcon aria-hidden='true' />
            </button>
          </div>
        </div>
        <div
          key={slide.title}
          className='organisation-splash-copy'
          aria-live={autoplay ? 'off' : 'polite'}
        >
          <h3 className='organisation-splash-title'>{slide.title}</h3>
          <p className='organisation-splash-text'>{slide.text}</p>
        </div>
        <button
          type='button'
          className='organisation-splash-button'
          onClick={onCreate}
        >
          Create your organisation
        </button>
      </section>
    </div>
  );
};

export default OrganisationSplash;
